import { forwardRef, useCallback, useMemo } from "react";

import { useControllableState } from "@aircall/hooks";
import { Flex } from "@aircall/tractor";

import { SLIDER_HEIGHT, THUMB_SIZE } from "./Slider.constants";
import {
  SLIDER_MARK_STATE,
  SliderMarksProps,
  SliderProps,
} from "./Slider.decl";
import { SliderDataList, SliderMark, SliderRange } from "./Slider.styled";

function getMarkState(markValue: number, value: number): SLIDER_MARK_STATE {
  if (markValue === value) {
    return SLIDER_MARK_STATE.EQUAL;
  }
  if (markValue > value) {
    return SLIDER_MARK_STATE.GREATER;
  }
  return SLIDER_MARK_STATE.SMALLER;
}

const SliderMarks = ({
  marks,
  value,
  min,
  max,
  disabled,
  id,
  hideThumb,
}: SliderMarksProps) => {
  if (!marks || marks.length === 0) {
    return null;
  }

  return (
    <SliderDataList id={id}>
      {marks.map(({ value: markValue, label }) => {
        const state = getMarkState(markValue, value ?? 0);

        return (
          <SliderMark
            key={markValue}
            value={markValue}
            left={`${((markValue - min) / max - min) * 100}%`}
            state={state}
            disabled={disabled}
            hideThumb={hideThumb}
          >
            {label}
          </SliderMark>
        );
      })}
    </SliderDataList>
  );
};

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      id,
      name,
      min = 0,
      max = 100,
      value,
      onChange,
      onMouseUp,
      onMouseDown,
      defaultValue = 0,
      step,
      marks,
      disabled = false,
      hideThumb = false,
      "data-test": dataTest = "slider",
    },
    ref
  ) => {
    const marksId = id ? `${id}-marks` : "marks";
    const [sliderValue, setSliderValue] = useControllableState({
      prop: value,
      defaultProp: defaultValue,
      onChange,
    });

    const progress = useMemo(
      () =>
        sliderValue ? (((sliderValue ?? 0) - min) / (max - min)) * 100 : 0,
      [max, min, sliderValue]
    );
    const onInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSliderValue(Number(e.target.value));
      },
      [setSliderValue]
    );

    return (
      <Flex
        gap={`${(THUMB_SIZE - SLIDER_HEIGHT) / 2}px`}
        flexDirection="column"
        cursor={disabled ? "not-allowed" : "pointer"}
        userSelect={disabled ? "none" : "auto"}
        data-test={dataTest}
      >
        <SliderMarks
          min={min}
          max={max}
          marks={marks}
          value={value}
          disabled={disabled}
          id={marksId}
          hideThumb={hideThumb}
        />
        <SliderRange
          data-test={`${dataTest}-input`}
          onMouseUp={onMouseUp}
          onMouseDown={onMouseDown}
          onChange={onInputChange}
          name={name}
          ref={ref}
          progress={progress}
          type="range"
          step={step}
          min={min}
          max={max}
          value={sliderValue}
          id={id}
          list={marks ? marksId : undefined}
          hideThumb={hideThumb}
          disabled={disabled}
        />
      </Flex>
    );
  }
);
